import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import { SEO } from "../components/SEO";

interface ContactFormData {
  name: string;
  email: string;
  category: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    category: "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const categories = [
    { value: "", label: "選択してください" },
    { value: "inquiry", label: "サービスに関する質問" },
    { value: "bug", label: "不具合の報告" },
    { value: "feature", label: "機能のリクエスト" },
    { value: "other", label: "その他" },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "お名前を入力してください";
    }

    if (!formData.email.trim()) {
      newErrors.email = "メールアドレスを入力してください";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "正しいメールアドレスを入力してください";
    }

    if (!formData.category) {
      newErrors.category = "お問い合わせ種類を選択してください";
    }

    if (!formData.message.trim()) {
      newErrors.message = "メッセージを入力してください";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "メッセージは10文字以上入力してください";
    } else if (formData.message.trim().length > 2000) {
      newErrors.message = "メッセージは2000文字以内で入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5173";

      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: "success",
          message: "お問い合わせを送信しました。ご連絡ありがとうございます。",
        });
        setFormData({
          name: "",
          email: "",
          category: "",
          message: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message:
            data.message ||
            "送信に失敗しました。しばらくしてから再度お試しください。",
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "送信に失敗しました。インターネット接続を確認してください。",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="お問い合わせ"
        description="就活ダッシュボードに関するご質問、不具合の報告、機能のリクエストなど、お気軽にお問い合わせください。"
        canonical="/contact"
        keywords={[
          "お問い合わせ",
          "サポート",
          "ヘルプ",
          "質問",
          "問い合わせフォーム",
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "お問い合わせ",
          description: "就活ダッシュボードへのお問い合わせページ",
        }}
      />
      <div className="min-h-screen bg-gray-50">
        {/* ヘッダーバー */}
        <header className="bg-white border-b border-gray-200 py-4 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-8 flex justify-between items-center">
            <div>
              <h1 className="m-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-indigo-500 to-purple-600">
                就活ダッシュボード
              </h1>
              <p className="m-0 text-xs text-gray-500 mt-1">
                就活企業とスケジュールを管理
              </p>
            </div>
            <button
              onClick={() => navigate("/about")}
              className="py-2 px-6 bg-gray-100 text-gray-700 border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-gray-200"
            >
              トップに戻る
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto p-8 py-12">
          {/* タイトル */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              お問い合わせ
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              ご質問、ご要望、不具合報告など、お気軽にお問い合わせください。
            </p>
          </div>

          {/* お問い合わせフォーム */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <form onSubmit={handleSubmit}>
              {/* お名前 */}
              <div className="mb-6">
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="山田 太郎"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* メールアドレス */}
              <div className="mb-6">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="example@email.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* お問い合わせ種類 */}
              <div className="mb-6">
                <label
                  htmlFor="category"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  お問い合わせ種類 <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.category ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              {/* メッセージ */}
              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  メッセージ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none ${
                    errors.message ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="お問い合わせ内容を詳しくご記入ください"
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* ステータスメッセージ */}
              {submitStatus.type && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === "success"
                      ? "bg-green-50 border border-green-200 text-green-700"
                      : "bg-red-50 border border-red-200 text-red-700"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              {/* 送信ボタン */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 border-none rounded-lg text-white text-base font-bold cursor-pointer transition-all ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg"
                }`}
              >
                {isSubmitting ? "送信中..." : "送信する"}
              </button>
            </form>
          </div>
        </main>

        {/* フッター */}
        <Footer />
      </div>
    </>
  );
};

export default ContactPage;
